import { Controller } from "@hotwired/stimulus"

/**
 * Ingredient Quantity Controller
 *
 * Maneja steppers (+/−) para categorías con allow_repeat=true.
 * Cada ingrediente tiene un stepper con cantidad. El total de
 * cantidades en la categoría no puede exceder max_ingredients.
 *
 * Values:
 *   max: Number — máximo total de selecciones en la categoría (-1 = ilimitado)
 *   required: Boolean — si la categoría es obligatoria
 *   categoryId: Number
 *   categoryName: String
 *
 * Targets:
 *   stepper — cada row de ingrediente (tiene data-ingredient-id, data-price)
 *   counter — el <span> que muestra la cantidad de ese ingrediente
 *   totalCounter — badge general "N/M" de la categoría
 */
export default class extends Controller {
  static targets = ["stepper", "counter", "totalCounter"]
  static values = {
    max: { type: Number, default: -1 },
    required: { type: Boolean, default: false },
    categoryId: Number,
    categoryName: String,
    comboInstance: { type: String, default: "" }
  }

  connect() {
    this.updateUI()
  }

  increment(event) {
    const row = event.currentTarget.closest("[data-ingredient-qty-target='stepper']")
    const counter = row.querySelector("[data-ingredient-qty-target='counter']")
    const hiddenInput = row.querySelector("input[type='hidden']")
    let qty = parseInt(counter.textContent) || 0

    // Check if we can add more
    if (this.maxValue !== -1 && this.totalSelected() >= this.maxValue) return

    qty++
    counter.textContent = qty
    hiddenInput.value = qty
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }))
    this.updateUI()
    this.dispatchPriceChange()
  }

  decrement(event) {
    const row = event.currentTarget.closest("[data-ingredient-qty-target='stepper']")
    const counter = row.querySelector("[data-ingredient-qty-target='counter']")
    const hiddenInput = row.querySelector("input[type='hidden']")
    let qty = parseInt(counter.textContent) || 0

    if (qty <= 0) return

    qty--
    counter.textContent = qty
    hiddenInput.value = qty
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }))
    this.updateUI()
    this.dispatchPriceChange()
  }

  totalSelected() {
    return this.counterTargets.reduce((sum, el) => sum + (parseInt(el.textContent) || 0), 0)
  }

  updateUI() {
    const total = this.totalSelected()
    const atMax = this.maxValue !== -1 && total >= this.maxValue

    // Update + buttons (disable when at max)
    this.stepperTargets.forEach(row => {
      const plusBtn = row.querySelector("[data-action*='increment']")
      const minusBtn = row.querySelector("[data-action*='decrement']")
      const counter = row.querySelector("[data-ingredient-qty-target='counter']")
      const qty = parseInt(counter.textContent) || 0

      if (plusBtn) plusBtn.disabled = atMax
      if (minusBtn) minusBtn.disabled = qty <= 0
    })

    // Update total counter badge
    if (this.hasTotalCounterTarget) {
      if (this.maxValue !== -1) {
        this.totalCounterTarget.textContent = `${total}/${this.maxValue}`
      } else {
        this.totalCounterTarget.textContent = total > 0 ? total : ""
      }
    }
  }

  dispatchPriceChange() {
    // Emit event so product-form can recalculate total price
    this.dispatch("priceChange", {
      detail: { additionalPrice: this.calculateAdditionalPrice() }
    })
  }

  calculateAdditionalPrice() {
    let total = 0
    this.stepperTargets.forEach(row => {
      const counter = row.querySelector("[data-ingredient-qty-target='counter']")
      const qty = parseInt(counter.textContent) || 0
      const price = parseFloat(row.dataset.price) || 0
      total += qty * price
    })
    return total
  }

  // Called from product_form_controller submit validation
  validate() {
    if (this.requiredValue && this.totalSelected() === 0) {
      const prefix = this.comboInstanceValue ? `${this.comboInstanceValue} — ` : ""
      return `${prefix}${this.categoryNameValue}`
    }
    return null
  }
}
